import { Link, NavLink, Outlet } from 'react-router-dom';
import type { ReactNode } from 'react';
import { SyncIndicator } from '../../features/sync/ui/SyncIndicator';
import { IosInstallHint } from '../../features/sync/ui/IosInstallHint';
import { TaskEditorPanel } from '../../features/tasks';
import { useAutoArchiveCompleted } from '../../features/tasks/lib/useTaskActions';
import { NavList, desktopNavItems, primaryNavItems } from './NavList';
import { ProfileMenu, SyncFooter } from './ProfileMenu';

function MobileNavLink({
  to,
  label,
  children,
}: {
  to: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex min-h-12 flex-col items-center justify-center gap-0.5 rounded-xl px-1 py-1 text-[11px] ${
          isActive ? 'bg-tg-accent-soft text-tg-accent' : 'text-tg-text-secondary'
        }`
      }
    >
      {children}
      <span>{label}</span>
    </NavLink>
  );
}

export function AppLayout() {
  // Авто-архив выполненных задач с due_date < сегодня (раз в день).
  useAutoArchiveCompleted();

  return (
    <div className="min-h-dvh bg-tg-bg-deep text-tg-text">
      <div className="mx-auto flex min-h-dvh w-full max-w-6xl">
        <aside className="sticky top-0 hidden h-dvh w-64 shrink-0 flex-col border-r border-tg-border-soft bg-tg-bg px-4 py-5 md:flex">
          <div className="mb-6">
            <Link to="/today" className="text-xl font-bold tracking-tight text-tg-text">
              Brainote
            </Link>
            <p className="mt-1 text-xs text-tg-text-muted">Личный задачник</p>
          </div>

          <div className="flex-1 overflow-y-auto">
            <NavList items={desktopNavItems} />
          </div>

          <div className="mt-4 space-y-3 border-t border-tg-border-soft pt-4">
            <ProfileMenu />
            <SyncFooter />
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-30 border-b border-tg-border-soft bg-tg-bg/95 backdrop-blur">
            <div className="flex items-center justify-between gap-3 px-4 py-3 md:px-6">
              <Link to="/today" className="text-lg font-bold text-tg-text md:hidden">
                Brainote
              </Link>
              <div className="hidden md:block">
                <SyncIndicator />
              </div>
              <div className="flex items-center gap-2 md:hidden">
                <SyncIndicator />
                {/* Меню: привычки, проекты, теги, настройки, админ */}
                <ProfileMenu dropdownDirection="top" compact />
              </div>
            </div>
          </header>

          <main className="flex-1 pb-28 md:pb-10">
            <IosInstallHint />
            <Outlet />
          </main>

          <nav
            className="fixed inset-x-0 bottom-0 z-40 border-t border-tg-border-soft bg-tg-bg/98 backdrop-blur md:hidden"
            aria-label="Мобильная навигация"
          >
            <ul className="mx-auto flex max-w-lg items-stretch justify-between gap-1 px-2 pb-[max(0.35rem,env(safe-area-inset-bottom))] pt-1">
              {primaryNavItems.map((item) => (
                <li key={item.to} className="flex-1">
                  <MobileNavLink to={item.to} label={item.label}>
                    {item.icon}
                  </MobileNavLink>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
      <TaskEditorPanel />
    </div>
  );
}
